import { useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appVersionsCopy from '@/i18n/ns/app-versions';
import readyCopy from '@/i18n/ns/ready';
import {
  actionsFor,
  clampPercent,
  comparePair,
  orderVersions,
  type AppVersionRow,
} from './versions-model';
import './versions-tab.css';
import { Button } from '@/components/ui/button';
import { OriginTag, StatusPill } from '@/components/ui/kit';

export type VersionsTabProps = {
  versions: readonly AppVersionRow[];
  dark: boolean;
  canEdit: boolean;
  canPublish: boolean;
  /** Formats a stored instant for the row ("Wed 30 Sep 14:20"). */
  formatAt: (iso: string) => string;
  onPublish: (revision: number) => void;
  onRestore: (revision: number) => void;
  onOpenRun: (runId: string) => void;
  onView?: (revision: number) => void;
};

export function VersionsTab(props: VersionsTabProps) {
  const copy = useNamespaceCopy(appVersionsCopy);
  const ready = useNamespaceCopy(readyCopy);
  const rows = orderVersions(props.versions);
  const pair = comparePair(props.versions);
  if (rows.length === 0) return <p data-testid="versions-empty">{copy.empty}</p>;
  return (
    <div className="pc-versions-layout" data-testid="app-versions">
      <ul className="pc-versions-list">
        {rows.map((row) => {
          const actions = actionsFor(row, props.canEdit, props.canPublish);
          const preview = row.preview?.[props.dark ? 'dark' : 'light'];
          return (
            <li key={row.revision} data-draft={row.isDraft ? 'true' : undefined}>
              <button
                type="button"
                className="pc-versions-main"
                data-testid="version-row"
                data-revision={row.revision}
                onClick={() => props.onView?.(row.revision)}
              >
                {preview ? <img src={preview} alt="" /> : <span className="pc-versions-tile" />}
                <span className="pc-versions-text">
                  <strong>
                    {formatCopy(row.isDraft ? copy.draftTitle : copy.versionTitle, {
                      n: String(row.revision),
                    })}
                  </strong>
                  <small>{describe(row, copy, props.formatAt, ready.created)}</small>
                  {row.origin.kind === 'builder' && row.origin.model && (
                    <small data-testid="version-built-with">
                      {formatCopy(copy.builtWith, { model: row.origin.model })}
                    </small>
                  )}
                  {(row.origin.kind === 'agent' || row.origin.kind === 'terminal') && (
                    <span data-testid="version-origin">
                      <OriginTag label={row.origin.label} />
                    </span>
                  )}
                </span>
              </button>
              <span className="pc-versions-side">
                {row.isDraft && <StatusPill>{ready.draft}</StatusPill>}
                {row.live && <StatusPill tone="success">{ready.ready}</StatusPill>}
                {actions.publish && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="version-publish"
                    onClick={() => props.onPublish(row.revision)}
                  >
                    {ready.markThisReady}
                  </Button>
                )}
                {actions.restore && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="version-restore"
                    data-revision={row.revision}
                    onClick={() => props.onRestore(row.revision)}
                  >
                    {copy.restore}
                  </Button>
                )}
                {actions.openRun && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="version-open-run"
                    onClick={() => {
                      const origin = row.origin;
                      if (origin.kind !== 'builder' && origin.runId) props.onOpenRun(origin.runId);
                    }}
                  >
                    {copy.openRun}
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {pair && <CompareSlider pair={pair} dark={props.dark} />}
    </div>
  );
}

function describe(
  row: AppVersionRow,
  copy: Readonly<Record<keyof typeof import('@/i18n/ns/app-versions').appVersionsCopy.en, string>>,
  formatAt: (iso: string) => string,
  created: string,
) {
  // The first revision is Created, not Edited (P3A-D04).
  const parts: string[] = row.revision === 1 ? [created, row.summary] : [row.summary];
  if (row.origin.kind === 'builder' && row.origin.forPerson) {
    parts.push(formatCopy(copy.builtBy, { who: row.origin.forPerson }));
  }
  parts.push(formatAt(row.at));
  if (row.basedOn) parts.push(formatCopy(copy.basedOn, { n: String(row.basedOn) }));
  // The publisher is a separate fact from the origin: never folded into who built it.
  if (row.published) {
    parts.push(
      formatCopy(copy.publishedBy, { who: row.published.by, at: formatAt(row.published.at) }),
    );
  }
  return parts.join(' · ');
}

function CompareSlider({
  pair,
  dark,
}: {
  pair: { from: AppVersionRow; to: AppVersionRow };
  dark: boolean;
}) {
  const copy = useNamespaceCopy(appVersionsCopy);
  const [percent, setPercent] = useState(50);
  const key = dark ? 'dark' : 'light';
  const before = pair.from.preview?.[key];
  const after = pair.to.preview?.[key];
  const values = { from: String(pair.from.revision), to: String(pair.to.revision) };
  return (
    <section className="pc-compare" data-testid="version-compare">
      <header>
        <h3>{formatCopy(copy.compareTitle, values)}</h3>
        {before && after && <span>{copy.compareHint}</span>}
      </header>
      {before && after ? (
        <div className="pc-compare-stage" style={{ ['--pc-compare' as string]: `${percent}%` }}>
          <img src={after} alt="" className="pc-compare-after" />
          <img src={before} alt="" className="pc-compare-before" />
          <span className="pc-compare-tag" data-side="from">
            {formatCopy(copy.compareFrom, { n: values.from })}
          </span>
          <span className="pc-compare-tag" data-side="to">
            {formatCopy(copy.compareTo, { n: values.to })}
          </span>
        </div>
      ) : (
        <p data-testid="version-compare-empty">{copy.noPreview}</p>
      )}
      {before && after && (
        <input
          type="range"
          min={0}
          max={100}
          value={percent}
          aria-label={formatCopy(copy.compareLabel, values)}
          data-testid="version-compare-range"
          onChange={(event) => setPercent(clampPercent(Number(event.target.value)))}
          onInput={(event) => setPercent(clampPercent(Number(event.currentTarget.value)))}
        />
      )}
    </section>
  );
}
